import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    Input,
    model,
    OnChanges,
    OnInit,
    inject,
    signal,
    SimpleChanges,
    ViewEncapsulation,
    effect,
} from '@angular/core';
import {
    IItemsListFilter,
    IItemsListFiltersData,
    ISelectedFilterItem,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { FiItemsListFilterService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { ItemsListFilterSelectionMapperService } from '../services/items-list-filter-selection-mapper.service';
import { ItemsListFilterDialogSnackbarService } from '../services/items-list-filter-dialog-snackbar.service';

@Component({
    selector: 'fi-items-list-filter',
    templateUrl: './fi-items-list-filter.component.html',
    styleUrls: ['./fi-items-list-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-items-list-filter' },
    standalone: false,
})
export class FiItemsListFilterComponent implements OnInit, OnChanges {
    @Input() itemsCountText: string;
    @Input() itemsListType: FiItemsListType;
    @Input() categoryItems: ISideListOptionsItem[];
    @Input() changedItems: ISideListOptionsItem[];
    @Input() tagItems: ISideListOptionsItem[];
    @Input() hasSuperAdminAccess: boolean;

    public isSuperAdminAccess = model<boolean>();
    public currentFilter = model<IItemsListFilter>();
    public isFilterSettingsShown = model<boolean>();

    private itemsListFilterService = inject(FiItemsListFilterService);
    private itemsListFilterHelperService = inject(FiItemsListFilterHelperService);
    private appTextService = inject(AppTextService);
    private cdr = inject(ChangeDetectorRef);
    private itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);
    private itemsListFilterSelectionMapperService = inject(ItemsListFilterSelectionMapperService);

    protected filtersData = signal<IItemsListFiltersData>(undefined);

    protected selectedFilterItems = signal<ISelectedFilterItem[]>(undefined);
    protected selectedItemsCountByFilter = computed(() => {
        const currentFilter = this.currentFilter();
        if (!isDefined(currentFilter) || !isDefined(currentFilter.Settings)) {
            return 0;
        }

        const settings = currentFilter.Settings;

        const arrayFilters = [
            settings.CategoryFilterTypes,
            settings.TagIds,
            settings.UserIds,
            settings.BusinessUnitIds,
            settings.AreaIds,
            settings.ClusterIds,
            settings.BusinessAreaIds,
        ];

        const optionalFilters = [settings.ChangedOnFilterType, settings.ChangedOnRange];

        return (
            arrayFilters.reduce((count, filter) => count + (filter?.length ?? 0), 0) + optionalFilters.filter(isDefined).length
        );
    });

    private selectedFilterItemsByFilter = computed<ISelectedFilterItem[]>(() => {
        const currentFilter = this.currentFilter();
        const filtersData = this.filtersData();
        if (!isDefined(filtersData) || !isDefined(currentFilter)) {
            return [];
        }
        const selectedFilterItems = this.itemsListFilterSelectionMapperService.getSelectedFilterItems({
            currentFilterSettings: currentFilter.Settings,
            tagItems: this.tagItems,
            categoryItems: this.categoryItems,
            accessRightFilterCollection: filtersData.AccessRightFilterCollection,
            changedItems: this.changedItems,
        });
        return selectedFilterItems;
    });

    private selectedFilterItemsByFilterEffect = effect(() => {
        const selectedFilterItems = this.selectedFilterItemsByFilter();
        this.selectedFilterItems.set(selectedFilterItems);
    });

    protected loading = signal<boolean>(false);

    public ngOnInit(): void {
        this.checkLoadFiltersData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.categoryItems || changes.tagItems || changes.changedItems) {
            this.setSelectedFilters();
        }
        this.cdr.markForCheck();
    }

    private loadData(): Promise<void> {
        return this.itemsListFilterService.getFiltersData(this.itemsListType).then((data) => {
            this.filtersData.set(data);
            this.setSelectedFilters();
            this.cdr.markForCheck();
        });
    }

    private setSelectedFilters(): void {
        this.selectedFilterItems.set(this.selectedFilterItemsByFilter());
    }

    protected saveCurrentFilter(): void {
        const currentFilter = this.currentFilter();
        if (currentFilter.Id) {
            this.saveFilter(currentFilter, () =>
                this.itemsListFilterDialogSnackbarService.showUpdateSuccessSnackbar(currentFilter.Name),
            );
        } else {
            this.showSaveNewFilterDialog();
        }
    }

    private saveFilter(filterData: IItemsListFilter, showNotificationDialog?: () => void): void {
        this.itemsListFilterService.saveFilter(this.itemsListType, filterData).then((id) => {
            const filtersData = this.filtersData();
            if (!filterData.Id) {
                filtersData.Filters.push(filterData);
            }
            filterData.Id = id;

            const updatedFilterIndex: number = filtersData.Filters.findIndex((filter: IItemsListFilter) => {
                return filter.Id === filterData.Id;
            });
            filtersData.Filters[updatedFilterIndex] = cloneDeep(filterData);
            this.filtersData.set(filtersData);

            if (filterData.Id === this.currentFilter().Id) {
                this.triggerFilterChanged(filterData);
            }

            if (showNotificationDialog) showNotificationDialog();

            this.cdr.markForCheck();
        });
    }

    protected deleteFilter(filter: IItemsListFilter): void {
        this.itemsListFilterService.deleteFilters(this.itemsListType, [filter.Id]).then(() => {
            this.filtersData.update((fd) => ({
                ...fd,
                Filters: fd.Filters.filter((f: IItemsListFilter) => f.Id !== filter.Id),
            }));
            if (this.currentFilter().Id === filter.Id) {
                this.selectedFilterItems.set([]);
                this.triggerFilterChanged(this.itemsListFilterService.getNewFilterInstance());
            }
            this.cdr.markForCheck();
        });
    }

    protected applyFilter(filter: IItemsListFilter): void {
        this.triggerFilterChanged(filter);
        this.setSelectedFilters();
    }

    protected clearAll(): void {
        this.triggerFilterChanged(this.itemsListFilterService.getNewFilterInstance());
    }

    protected triggerFilterChanged(newFilter: IItemsListFilter) {
        this.currentFilter.set(cloneDeep(newFilter));
        this.setSelectedFilters();
        this.cdr.detectChanges();
    }

    private showSaveNewFilterDialog(): void {
        const currentFilter = this.currentFilter();
        this.itemsListFilterDialogSnackbarService
            .getFilterNameDialog(
                currentFilter.Name,
                this.appTextService.get('save_filter'),
                'fal fa-bookmark BrandedColor',
            )
            .onYesButtonClick((newFilterName: string) => {
                currentFilter.Name = newFilterName;
                this.currentFilter.update((cf) => ({
                    ...cf,
                    Name: newFilterName,
                }));
                this.saveFilter(currentFilter, () =>
                    this.itemsListFilterDialogSnackbarService.showSaveSuccessSnackbar(currentFilter.Name),
                );
            });
    }

    protected openRenameSavedFilterDialog(savedFilterData: IItemsListFilter): void {
        this.itemsListFilterDialogSnackbarService
            .getFilterNameDialog(
                savedFilterData.Name,
                this.appTextService.get('filter.rename_filter'),
                'fal fa-pen-to-square BrandedColor',
            )
            .onYesButtonClick((newFilterName: string) => {
                savedFilterData.Name = newFilterName;
                if (savedFilterData.Id === this.currentFilter().Id) {
                    this.triggerFilterChanged(savedFilterData);
                }
                this.saveFilter(savedFilterData);
            });
    }

    protected openChangeSavedFilterDialog(savedFilterData: IItemsListFilter): void {
        const savedFilterSelectedItem: ISelectedFilterItem[] = this.selectedFilterItemsByFilter();
        this.itemsListFilterDialogSnackbarService
            .getEditFilterDialog(savedFilterData.Name, savedFilterSelectedItem)
            .onYesButtonClick((result: ISelectedFilterItem[]) => {
                if (!result.length) {
                    this.showNoFilterItemsWarningDialog(savedFilterData);
                } else {
                    savedFilterData = this.itemsListFilterHelperService.getFilterDataBySelectedItems(
                        savedFilterData,
                        savedFilterSelectedItem,
                        result,
                    );
                    this.saveFilter(savedFilterData);
                    if (this.currentFilter().Id === savedFilterData.Id) {
                        this.triggerFilterChanged(this.currentFilter());
                        this.setSelectedFilters();
                    }
                }
            });
    }

    private showNoFilterItemsWarningDialog(filterData: IItemsListFilter) {
        this.itemsListFilterDialogSnackbarService
            .getNoFilterItemsWarningDialog()
            .onYesButtonClick(() => this.deleteFilter(filterData));
    }

    protected openDeleteSavedFilterDialog(savedFilterData: IItemsListFilter): void {
        this.itemsListFilterDialogSnackbarService
            .getConfirmDeleteSavedFilterDialog()
            .onYesButtonClick(() => this.deleteFilter(savedFilterData));
    }

    private checkLoadFiltersData() {
        if (this.isFilterSettingsShown() && !this.filtersData() && !this.loading()) {
            this.loading.set(true);
            this.loadData().finally(() => this.loading.set(false));
        }
    }

    protected onIsFilterSettingsShownChange(isFilterSettingsShown: boolean): void {
        this.isFilterSettingsShown.set(isFilterSettingsShown);
        this.checkLoadFiltersData();
    }
}
